import { useEffect, useMemo, useState } from 'react';
import { setReceiverApplicationState, useCastReceiver } from '../lib/useCastReceiver.js';
import { CouchIcon } from '../components/CouchIcon.js';
import { Host, type HostBoot } from './Host.js';
import { parsePresentUrl, receiverWaiting } from './receiverModel.js';
import styles from './Receiver.module.css';

/**
 * The Cast Web Receiver app: the page registered with the Cast Developer
 * Console that the cast device itself loads. It waits for the sender's one
 * message — the TV URL — and then mounts the ordinary `Host` renderer with
 * that boot, so the cast device runs exactly what a browser at `/host` would,
 * room pairing and casino handoff included. `?url=` short-circuits the cast
 * channel, which is how a plain desktop browser (and the jsdom tier) boots it
 * by hand.
 */
export function Receiver() {
  const { castReady, presentUrl: castPresent } = useCastReceiver();
  const [manual] = useState<string | null>(() => {
    const url = new URLSearchParams(window.location.search).get('url');
    if (!url) return null;
    window.history.replaceState(null, '', '/receiver');
    return url;
  });
  const presentUrl = manual ?? castPresent;
  const boot = useMemo<HostBoot | null>(
    () => (presentUrl ? parsePresentUrl(presentUrl) : null),
    [presentUrl],
  );

  useEffect(() => {
    if (!boot) return;
    setReceiverApplicationState('TV Casino — on the table');
  }, [boot]);

  if (boot) return <Host boot={boot} />;

  if (presentUrl && !boot) {
    return (
      <main className={styles.screen}>
        <h1 className={styles.brand}>
          <CouchIcon className={styles.brandIcon} />TV Casino Night
        </h1>
        <p className={styles.line}>The cast request did not carry a table.</p>
        <p className={styles.hint}>Press Cast on the host page again to retry.</p>
      </main>
    );
  }

  const waiting = receiverWaiting(castReady);
  return (
    <main className={styles.screen}>
      <h1 className={styles.brand}>
        <CouchIcon className={styles.brandIcon} />TV Casino Night
      </h1>
      <p className={styles.line}>{waiting.line}</p>
      <p className={styles.hint}>{waiting.hint}</p>
    </main>
  );
}
